import { ApiResponse } from "../../services/base/apiResponse";
import {
  AdminAppStudentPaymentsResponse,
  StudentInfoAdminResponse,
  StudentSemesterAttestationResponse,
} from "../../models/dtos";
import { studentsApi } from "../../services/students/studentsApi";
import { Button } from "primereact/button";
import { paymentsApi } from "../../services/payments/paymentsApi";
import { useEffect, useRef, useState } from "react";
import { InputNumber } from "primereact/inputnumber";
import { Card } from "primereact/card";
import SemesterAttestationForm from "./SemesterAttestationForm";
import PaymentsTable from "./PaymentsTable";
import AttestationsTable from "./AttestationsTable";
import { currenciesApi } from "../../services/nomenclatures/currenciesApi";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Toast } from "primereact/toast";

interface StudentPaymentProps {
  student?: ApiResponse<StudentInfoAdminResponse>;
}

const PaymentsInformation = ({ student }: StudentPaymentProps) => {
  const toast = useRef<Toast>(null);
  const { data: payments, refetch: refetchPayments } = studentsApi.useGetStudentPaymentsQuery(
    student?.data?.studentId ?? 0,
  );
  const { data: studentAttestations, refetch: refetchAttestations } =
    studentsApi.useGetStudentSemesterAttestationsQuery(student?.data?.studentId ?? 0);

  const { data: currencies } = currenciesApi.useGetCurrenciesQuery();
  const [addPayment, { isLoading: isAddingPayment }] = paymentsApi.useAddPaymentMutation();
  const [updatePayment] = paymentsApi.useUpdatePaymentMutation();
  const [deletePayment] = paymentsApi.useDeletePaymentMutation();
  const [deleteAttestation] = studentsApi.useDeleteStudentSemesterAttestationMutation();
  const [editingAttestation, setEditingAttestation] = useState<StudentSemesterAttestationResponse | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [currencyId, setCurrencyId] = useState<number | null>(null);

  useEffect(() => {
    setAmount(student?.data?.currentFeeAmount ?? student?.data?.feeAmount ?? null);
    setCurrencyId(student?.data?.currentFeeCurrencyId ?? null);
  }, [
    student?.data?.studentId,
    student?.data?.currentFeeAmount,
    student?.data?.currentFeeCurrencyId,
    student?.data?.feeAmount,
  ]);

  const [nextSemester, { isLoading: isNextSemesterLoading }] = studentsApi.useNextSemesterMutation();

  const handleNextSemester = async () => {
    if (!student?.data?.studentId) return;

    try {
      const response = await nextSemester({ studentId: student.data.studentId }).unwrap();
      toast.current?.show({
        severity: "success",
        summary: "Успешно",
        detail: response.message || "Студентът е записан за следващия семестър.",
      });
    } catch (error: unknown) {
      const data =
        typeof error === "object" && error !== null && "data" in error
          ? (error as { data?: { message?: string; errors?: string[] } }).data
          : undefined;
      const detail =
        data?.errors?.filter(Boolean).join(", ") ||
        data?.message ||
        "Записването за следващ семестър не беше извършено.";
      toast.current?.show({ severity: "error", summary: "Грешка", detail });
    }
  };

  const handleAddPayment = async () => {
    if (!student?.data?.studentId || !amount || amount <= 0 || !currencyId) return;

    try {
      const response = await addPayment({
        studentId: student.data.studentId,
        amount,
        currencyId,
      }).unwrap();
      await refetchPayments();
      toast.current?.show({ severity: "success", summary: "Успешно", detail: response.message });
    } catch (error: unknown) {
      const detail =
        typeof error === "object" && error !== null && "data" in error
          ? (error as { data?: { message?: string } }).data?.message
          : undefined;
      toast.current?.show({
        severity: "error",
        summary: "Грешка",
        detail: detail ?? "Плащането не беше запазено.",
      });
    }
  };

  const handleUpdatePayment = async (payment: AdminAppStudentPaymentsResponse) => {
    if (!payment.currencyId) {
      toast.current?.show({ severity: "error", summary: "Грешка", detail: "Изберете валута." });
      return;
    }

    try {
      await updatePayment({
        paymentId: payment.paymentId,
        amount: payment.amount ?? 0,
        startedOn: payment.startedOn ?? "",
        currencyId: payment.currencyId,
      }).unwrap();
      await refetchPayments();
      toast.current?.show({ severity: "success", summary: "Успешно", detail: "Плащането беше обновено." });
    } catch (error) {
      console.error("Failed to update payment:", error);
      toast.current?.show({ severity: "error", summary: "Грешка", detail: "Плащането не беше обновено." });
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    try {
      await deletePayment(paymentId).unwrap();
      await refetchPayments();
    } catch (error) {
      console.error("Failed to delete payment:", error);
    }
  };

  const handleEditAttestation = (attestation: StudentSemesterAttestationResponse) => {
    setEditingAttestation(attestation);
  };

  const handleDeleteAttestation = async (attestationId: number) => {
    try {
      await deleteAttestation(attestationId).unwrap();
      refetchAttestations();
    } catch (error) {
      console.error("Failed to delete attestation:", error);
    }
  };

  const handleAttestationSuccess = () => {
    setEditingAttestation(null);
    refetchAttestations();
  };

  return (
    <div className="card">
      <Card className="mb-4">
        <div className="flex flex-column">
          <div className="p-1 mb-4">
            <h4>Записване за следващ семестър</h4>
            <p>Натискането на бутона ще премести студента в следващ семестър и ще изпрати събитието към НАЦИД.</p>
          </div>
          <div className="p-1 mb-4">
            <Button
              label="Записване за следващия семестър"
              icon="pi pi-arrow-right"
              className="p-button-sm p-button-outlined"
              loading={isNextSemesterLoading}
              onClick={handleNextSemester}
            />
          </div>
        </div>
      </Card>

      <Card className="mb-4">
        <div className="p-1 mb-4">
          <h4>Заверка</h4>
          {student?.data?.studentId && (
            <>
              {editingAttestation ? (
                <SemesterAttestationForm
                  student={student.data}
                  onSuccess={handleAttestationSuccess}
                  initialValues={editingAttestation}
                />
              ) : (
                <SemesterAttestationForm student={student.data} onSuccess={refetchAttestations} />
              )}
            </>
          )}

          <AttestationsTable
            attestations={studentAttestations?.data || []}
            onEditAttestation={handleEditAttestation}
            onDeleteAttestation={handleDeleteAttestation}
          />
        </div>
      </Card>

      <Card className="mb-4">
        <div className="p-1 mb-4">
          <h4>Плащания</h4>
          <div className="flex flex-wrap align-items-end gap-2 mb-2">
            <div className="flex flex-column gap-2">
              <label htmlFor="paymentAmount">Сума</label>
              <InputNumber
                inputId="paymentAmount"
                value={amount}
                onValueChange={(e) => setAmount(e.value ?? null)}
                mode="decimal"
                min={0.01}
                minFractionDigits={2}
                maxFractionDigits={2}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="paymentCurrency">Валута</label>
              <Dropdown
                inputId="paymentCurrency"
                value={currencyId}
                options={currencies?.data ?? []}
                optionLabel="code"
                optionValue="currencyId"
                placeholder="Изберете валута"
                onChange={(e: DropdownChangeEvent) =>
                  setCurrencyId(typeof e.value === "number" ? e.value : null)
                }
              />
            </div>
          </div>
          <Button
            label="Добави плащане"
            icon="pi pi-plus"
            className="p-button-sm p-button-outlined"
            onClick={() => void handleAddPayment()}
            loading={isAddingPayment}
            disabled={!amount || amount <= 0 || !currencyId}
          />
        </div>

        {payments && (
          <PaymentsTable
            payments={payments.data}
            onDeletePayment={(paymentId) => void handleDeletePayment(paymentId)}
            onUpdatePayment={(payment) => void handleUpdatePayment(payment)}
            currencies={currencies?.data ?? []}
          />
        )}
      </Card>
      <Toast ref={toast} />
    </div>
  );
};

export default PaymentsInformation;
